import { LoadingButton } from "@mui/lab";
import { Box, Button, Dialog, DialogActions, DialogContent, TextField } from "@mui/material";
import PCheckbox from "components/PCheckbox";
import { useFormik } from "formik";
import { FirmWithLenses } from "generated/graphql";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { addLensesToFirm, createFirm, selectLenses, updateFirm } from "redux/slices/client";
import RequestStatus from "redux/slices/RequestStatus";
import { P } from "theme/typography";

// props
interface FirmFormProps {
  open: boolean;
  onClose: () => void;
  firmToEdit: FirmWithLenses | undefined;
}

const FirmForm = (props: FirmFormProps) => {
  const [status, setStatus] = useState<RequestStatus>(RequestStatus.IDLE);
  const snackpatch = useAppDispatchWithSnackbar();
  const lenses = useAppSelector(selectLenses);
  const [addLenses, setAddLenses] = useState<number[]>([]);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const form = useFormik({
    initialValues: {
      clientId: props.firmToEdit?.clientId || 0,
      name: props.firmToEdit?.name || "",
      address: props.firmToEdit?.address || "",
      attorneyName: props.firmToEdit?.attorneyName || "",
      phoneNumber: props.firmToEdit?.phoneNumber || "",
      externalId: props.firmToEdit?.externalId || "",
    },
    onSubmit: async () => {
      setStatus(RequestStatus.PENDING);
      if (props.firmToEdit) {
        snackpatch(updateFirm({ ...form.values, id: props.firmToEdit.id }), {
          successMessage: "Firm updated successfully",
          errorMessage: "Unable to update firm",
        }).then(() => {
          setStatus(RequestStatus.SUCCEEDED);
          props.onClose();
        });
      } else {
        try {
          const newFirm = await dispatch(createFirm(form.values)).unwrap();
          await dispatch(
            addLensesToFirm({
              firmId: newFirm.firm.upsertFirm.id,
              lensIds: addLenses,
            })
          ).unwrap();
          setStatus(RequestStatus.SUCCEEDED);
          enqueueSnackbar("Firm created successfully", { variant: "success" });
          props.onClose();
          form.resetForm();
        } catch (e) {
          setStatus(RequestStatus.FAILED);
          console.error(e);
          enqueueSnackbar("Unable to create firm or add lenses", { variant: "error" });
        }
      }
    },
  });

  useEffect(() => {
    form.setValues({
      clientId: props.firmToEdit?.clientId || 0,
      name: props.firmToEdit?.name || "",
      address: props.firmToEdit?.address || "",
      attorneyName: props.firmToEdit?.attorneyName || "",
      phoneNumber: props.firmToEdit?.phoneNumber || "",
      externalId: props.firmToEdit?.externalId || "",
    });
  }, [props.firmToEdit]);

  return (
    <Dialog
      open={props.open}
      onClose={() => {
        props.onClose();
      }}>
      <DialogContent>
        <P variant="h5">{props.firmToEdit ? "Edit" : "Add"} Firm</P>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            minWidth: "300px",
            mt: 2,
            gap: 2,
          }}>
          <TextField
            required
            label="Name"
            id="name"
            name="name"
            type="text"
            onChange={form.handleChange}
            value={form.values.name}
          />
          <TextField
            label="Address"
            id="address"
            name="address"
            type="text"
            onChange={form.handleChange}
            value={form.values.address}
          />
          <TextField
            label="Attorney Name"
            id="attorneyName"
            name="attorneyName"
            type="text"
            onChange={form.handleChange}
            value={form.values.attorneyName}
          />
          <TextField
            label="Phone Number"
            id="phoneNumber"
            name="phoneNumber"
            type="text"
            onChange={form.handleChange}
            value={form.values.phoneNumber}
          />
          <TextField
            label="External ID"
            id="externalId"
            name="externalId"
            type="text"
            onChange={(e) => {
              if (!props.firmToEdit) {
                form.setFieldValue("externalId", e.target.value.replace(/\s/g, "").toLowerCase());
              } else {
                form.setFieldValue("externalId", e.target.value);
              }
            }}
            value={form.values.externalId}
            helperText={!props.firmToEdit ? "External ID must not contain spaces and be lowercase" : undefined}
          />

          {props.firmToEdit === undefined && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "400px",
                mt: 2,
              }}>
              <P
                sx={{
                  mb: 1,
                }}>
                Select lenses to attach to this firm
              </P>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  maxHeight: "300px",
                  overflow: "auto",
                }}>
                {lenses.map((lens) => (
                  <Box
                    key={lens.id}
                    sx={{
                      ml: 2,
                      width: "100%",
                    }}>
                    <PCheckbox
                      checked={addLenses.includes(lens.id)}
                      label={lens.name}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAddLenses([...addLenses, lens.id]);
                        } else {
                          setAddLenses(addLenses.filter((lensId) => lensId !== lens.id));
                        }
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            props.onClose();
          }}>
          Cancel
        </Button>
        <LoadingButton
          disabled={form.values.name === ""}
          loading={status === RequestStatus.PENDING}
          onClick={() => {
            form.handleSubmit();
          }}
          variant="contained">
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default FirmForm;
